import React, {useMemo, useState} from "react";
import {Button, Modal, Typography} from "antd";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {ComponentPropsFromRoute} from "../../../../../routers/routers";
import {useTranslate} from "../../../../../hooks/translate.hook";
import {HealthCheckerCheck} from "../../../../../models/health-checker/health-checker";
import {healthCheckerService} from "../../../../../services/health-checker/health-checker.service";
import {notifications} from "../../../../../services/notifications/notifications";
import {SmartTable} from "../../../../common/smart-table/smart-table";
import {SmartTableDataHandler} from "../../../../common/smart-table/smart-table.data-handler";
import {columnsFactory} from "./checks.columns";
import {HealthCheckerCheckModal} from "./checks.modal";
import "./checks.scss";

const {Text, Title} = Typography;

export const HealthCenterChecks: React.FC<ComponentPropsFromRoute> = () => {
    const t = useTranslate();
    const [modalOpen, setModalOpen] = useState<boolean>(false);
    const [editableCheck, setEditableCheck] = useState<HealthCheckerCheck | null>(null);

    const columns = useMemo(() => columnsFactory(t), [t]);
    const handler = useMemo(
        () => new SmartTableDataHandler<HealthCheckerCheck>((request) => healthCheckerService.getChecks(request)),
        []
    );

    const openCreateModal = () => {
        setEditableCheck(null);
        setModalOpen(true);
    };

    const openEditModal = (check: HealthCheckerCheck) => {
        setEditableCheck(check);
        setModalOpen(true);
    };

    const closeModal = () => {
        setModalOpen(false);
        setEditableCheck(null);
    };

    const removeCheck = (check: HealthCheckerCheck) => {
        Modal.confirm({
            title: t("HEALTH-CENTER.SETTINGS.CHECKS.REMOVE-TITLE", {
                defaultValue: "Delete check?",
            }),
            content: t("HEALTH-CENTER.SETTINGS.CHECKS.REMOVE-DESCRIPTION", {
                name: check.checkerName,
                defaultValue: 'The check "{{name}}" will be permanently deleted.',
            }),
            okText: t("REMOVE", {defaultValue: "Delete"}),
            cancelText: t("CANCEL", {defaultValue: "Cancel"}),
            okButtonProps: {danger: true},
            onOk: async () => {
                await healthCheckerService.removeCheck(check.id);
                await handler.reloadItemsWithLastRequest();
                notifications.successfully();
            },
        });
    };

    return (
        <section className="health-center-checks">
            <div className="health-center-checks__toolbar">
                <div>
                    <Title level={4}>
                        {t("HEALTH-CENTER.SETTINGS.CHECKS.TITLE", {defaultValue: "Checks"})}
                    </Title>
                    <Text type="secondary">
                        {t("HEALTH-CENTER.SETTINGS.CHECKS.DESCRIPTION", {
                            defaultValue: "Configure health checks, their groups and execution periods",
                        })}
                    </Text>
                </div>
                <Button
                    type="primary"
                    icon={<FontAwesomeIcon icon={["fas", "plus"]} />}
                    onClick={openCreateModal}
                >
                    {t("HEALTH-CENTER.SETTINGS.CHECKS.CREATE", {defaultValue: "Create check"})}
                </Button>
            </div>

            <SmartTable
                handler={handler}
                columns={columns}
                actions={[
                    {
                        icon: "edit",
                        title: t("EDIT", {defaultValue: "Edit"}),
                        onClick: openEditModal,
                    },
                    {
                        icon: "trash",
                        title: t("REMOVE", {defaultValue: "Delete"}),
                        onClick: removeCheck,
                    },
                ]}
            />

            {modalOpen ? (
                <HealthCheckerCheckModal
                    check={editableCheck}
                    onCancel={closeModal}
                    onComplete={() => handler.reloadItemsWithLastRequest()}
                />
            ) : null}
        </section>
    );
};
